/**
 * Progress Service
 * 
 * Handles all API calls for the Syllabus Progress Tracking System.
 * Uses the apiService for HTTP requests with proper authentication.
 */

import { apiService } from './apiService';
import {
  ProgressOverview,
  SubjectProgressDetail,
  ObjectiveProgressUpdate,
  ObjectiveProgressResponse,
  AssessmentStartResponse,
  AssessmentSubmission,
  AssessmentResultResponse,
  AssessmentHistoryItem,
  SubjectStudentsProgress,
  AdminProgressOverview,
  StudentProgressSummary,
} from '../types/progress';

// ============= STUDENT ENDPOINTS =============

/**
 * Get overall progress overview for the current student
 */
export async function getProgressOverview(): Promise<ProgressOverview> {
  const response = await apiService.get<ProgressOverview>('/progress/me/overview');
  return response.data;
}

/**
 * Get detailed hierarchical progress for a specific subject
 */
export async function getSubjectProgress(subjectId: string): Promise<SubjectProgressDetail> {
  const response = await apiService.get<SubjectProgressDetail>(`/progress/me/subject/${subjectId}`);
  return response.data;
}

/**
 * Update progress for a specific learning objective
 */
export async function updateObjectiveProgress(
  objectiveId: string,
  update: ObjectiveProgressUpdate
): Promise<ObjectiveProgressResponse> {
  const response = await apiService.post<ObjectiveProgressResponse>(
    `/progress/me/objective/${objectiveId}`,
    update
  );
  return response.data;
}

/**
 * Start an assessment for a topic or subtopic
 */
export async function startAssessment(
  sectionId: string,
  sectionType: 'topic' | 'subtopic' = 'topic'
): Promise<AssessmentStartResponse> {
  const response = await apiService.post<AssessmentStartResponse>(
    `/progress/me/assessments/${sectionId}/start?section_type=${sectionType}`,
    {}
  );
  return response.data;
}

/**
 * Submit answers for an assessment
 */
export async function submitAssessment(
  assessmentId: string,
  submission: AssessmentSubmission
): Promise<AssessmentResultResponse> {
  const response = await apiService.post<AssessmentResultResponse>(
    `/progress/me/assessments/${assessmentId}/submit`,
    submission
  );
  return response.data;
}

/**
 * Get the current user's assessment history
 */
export async function getAssessmentHistory(limit: number = 20): Promise<AssessmentHistoryItem[]> {
  const response = await apiService.get<AssessmentHistoryItem[]>(
    `/progress/me/assessments/history?limit=${limit}`
  );
  return response.data;
}

// ============= ADMIN ENDPOINTS =============

/**
 * Get admin dashboard overview of progress across all subjects and students
 */
export async function getAdminProgressOverview(): Promise<AdminProgressOverview> {
  const response = await apiService.get<AdminProgressOverview>('/progress/admin/overview');
  return response.data;
}

/**
 * Get progress of all students for a specific subject
 */
export async function getSubjectStudentsProgress(subjectId: string): Promise<SubjectStudentsProgress> {
  const response = await apiService.get<SubjectStudentsProgress>(
    `/progress/admin/subjects/${subjectId}/students`
  );
  return response.data;
}

/**
 * Get detailed progress for a specific student (admin view)
 */
export async function getStudentProgressDetail(studentId: string): Promise<ProgressOverview> {
  const response = await apiService.get<ProgressOverview>(`/progress/admin/students/${studentId}`);
  return response.data;
}

/**
 * Get detailed subject progress for a specific student (admin view)
 */
export async function getStudentSubjectProgress(
  studentId: string,
  subjectId: string
): Promise<SubjectProgressDetail> {
  const response = await apiService.get<SubjectProgressDetail>(
    `/progress/admin/students/${studentId}/subject/${subjectId}`
  );
  return response.data;
}

/**
 * Get a list of students who are behind schedule
 */
export async function getStudentsBehind(limit: number = 50): Promise<StudentProgressSummary[]> {
  const response = await apiService.get<StudentProgressSummary[]>(
    `/progress/admin/students-behind?limit=${limit}`
  );
  return response.data;
}

// ============= HELPER FUNCTIONS =============

/**
 * Calculate time since last activity
 */
export function getTimeSinceActivity(lastActivityAt: string | null): string {
  if (!lastActivityAt) {
    return 'No activity yet';
  }
  
  const lastActivity = new Date(lastActivityAt);
  const now = new Date();
  const diffMs = now.getTime() - lastActivity.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins} min ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
  
  return lastActivity.toLocaleDateString();
}

/**
 * Format date for display
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Calculate progress bar color based on percentage
 */
export function getProgressBarColor(percent: number): string {
  if (percent >= 80) return 'bg-emerald-500';
  if (percent >= 60) return 'bg-blue-500';
  if (percent >= 40) return 'bg-yellow-500';
  if (percent >= 20) return 'bg-orange-500';
  return 'bg-red-500';
}

// Export all functions as a service object for convenience
export const progressService = {
  // Student
  getProgressOverview,
  getSubjectProgress,
  updateObjectiveProgress,
  startAssessment,
  submitAssessment,
  getAssessmentHistory,
  // Admin
  getAdminProgressOverview,
  getSubjectStudentsProgress,
  getStudentProgressDetail,
  getStudentSubjectProgress,
  getStudentsBehind,
  // Helpers
  getTimeSinceActivity,
  formatDate,
  getProgressBarColor,
};

export default progressService;

